import cv from "@techstark/opencv-js";
import {recreateInferenceSession} from "./imgInpaint";
import '../../../assets/styles/tools/imageInPaint.css';
import '../../../assets/styles/App.css';
import React, {DragEvent, useState} from "react";
import {Upload} from "lucide-react";
import {BrandName, DemoBgImageList} from "../../../constants";
import {ImageInPaintEditor} from "./imageInPaintEditor";
import {useImageInPaintEditorInfoStore} from "../../../store/imageInPaintEditorInfoStore";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {useNotificationMessage} from "../../../hooks/NotificationContext";

export const ImageInPaint = () => {
    const {showAlert} = useAlertMessage();
    const {showLoadingNotification} = useNotificationMessage();

    const [isShowEditor, setIsShowEditor] = useState(false);
    const {setImageObj, setMaskBase64} = useImageInPaintEditorInfoStore();

    const [isDragging, setIsDragging] = useState(false);
    const dropZoneRef = React.createRef();

    const bgImgFileRef = React.createRef<HTMLInputElement>();// 图片文件上传ref

    cv["onRuntimeInitialized"] = async () => {// 异步初始化onnxruntime
        await recreateInferenceSession(showAlert, showLoadingNotification);
    }

    const uploadImgFile = (ev: any) => {
        const file = ev.currentTarget.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                setImageObj(image);
                setMaskBase64("");
                setIsShowEditor(true);
            };
        }
    }
    const uploadImg = () => {
        if (bgImgFileRef.current) {
            bgImgFileRef.current.value = '';// 重置文件输入值
            bgImgFileRef.current.click();
        }
    }

    const selectDemoImg = async (img: string) => {
        const blob = await fetch(img)
            .then(r => r.blob());
        if (!blob) return;

        const imageUrl = URL.createObjectURL(blob);
        const image = new Image();
        image.src = imageUrl;
        image.onload = () => {
            setImageObj(image);
            setMaskBase64("");
            setIsShowEditor(true);
        };
    }

    // 拖拽进入目标区域时显示蒙层
    const handleDragOver = (e: DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    // 拖拽离开目标区域时隐藏蒙层
    const handleDragLeave = (e: DragEvent) => {
        const dropZone = dropZoneRef.current;
        if (dropZone) {
            const relatedTarget = e.relatedTarget;

            // 如果鼠标移入的是 dropZone 或其子元素，则不隐藏蒙层
            if ((dropZone as any).contains(relatedTarget)) return;
        }

        setIsDragging(false);
    };

    // 释放图片时触发事件
    const handleDrop = async (e: DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            const blob = await fetch(URL.createObjectURL(file))
                .then(res => res.blob());
            if (!blob) return;

            const imageUrl = URL.createObjectURL(blob);
            const image = new Image();
            image.src = imageUrl;
            image.onload = () => {
                setImageObj(image);
                setMaskBase64("");
                setIsShowEditor(true);
            };
        }
    };

    return (
        <div className="flex flex-row">
            {isShowEditor &&
                <ImageInPaintEditor
                    closeEditorTable={() => setIsShowEditor(false)}
                />
            }
            {!isShowEditor &&
                <div className="i-in-paint">
                    <h1 className="mb-[2vh]">AI Magic Eraser Online for Free</h1>
                    <p className="pt-2 pb-8 text-base text-gray-500 text-center">
                        Clean up your photos like magic! {BrandName}’s AI Magic Eraser instantly deletes distractions—text, logos, watermarks, or photobombers—with just a click.
                    </p>
                    <div className="flex flex-row gap-[3%] justify-between">
                        {/*上传图片区域*/}
                        <div className="overflow-hidden flex-1">
                            <div className="i-upload-card-container">
                                <div className="upload-card">
                                    <div className="upload-box">
                                        <div
                                            className="upload-box-action"
                                            // @ts-ignore
                                            ref={dropZoneRef}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                        >
                                            {/* 灰色蒙层 */}
                                            {isDragging && (
                                                <div
                                                    className="flex absolute top-0 left-0 w-[100%] h-[100%] z-[2050] pointer-events-none justify-center items-center"
                                                    style={{backgroundColor: 'rgba(0, 0, 0, 0.3)'}}
                                                >Drop here</div>
                                            )}
                                            <div className="flex items-center justify-center w-[100%]">
                                                <div style={{width: '320px'}} className="bg-[#FF8D00] min-w-[320px] i-opt-button" onClick={uploadImg}>
                                                    <Upload style={{width: '18px', height: '18px'}} />Upload a photo
                                                </div>
                                            </div>
                                            <span className="w-[95%] text-[#7f8792] text-center text-[12px]">Or drop an image</span>
                                        </div>
                                        <div className="mb-[12px]">
                                            <div className="upload-box-demo-tag text-[#9da3ac]">
                                                <span className="mx-2 text-[12px]">Try with one of these</span>
                                            </div>
                                            <div className="flex gap-[8px] justify-center">
                                                {DemoBgImageList
                                                    .filter((_, index) => index < 6)
                                                    .map((img, index) => (
                                                        <div
                                                            onClick={() => selectDemoImg(img)}
                                                            className="relative flex flex-row w-[48px] h-[48px]
                                                             overflow-hidden rounded-[8px] cursor-pointer
                                                             border-transparent hover:border-[2px] hover:border-black hover:border-opacity-30"
                                                        >
                                                            <div
                                                                style={{
                                                                backgroundImage: `url('${img}')`,
                                                                backgroundSize: 'cover',
                                                                objectFit: 'contain',
                                                                width: '100%',
                                                                height: '100%'
                                                            }}/>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <input
                                    // @ts-ignore
                                    ref={bgImgFileRef}
                                    type="file"
                                    className="hidden"
                                    onChange={uploadImgFile}
                                    accept="image/png, image/jpeg, image/webp"
                                />
                            </div>
                        </div>
                        {/*演示*/}
                        <div className="shrink-0 w-[45%] mr-4">
                            <div className="pb-[66.6667%] h-0 rounded-[20px] relative w-full h-auto overflow-hidden">
                                <div className="absolute top-0 left-0 w-full h-full">
                                    <div
                                        className="flex box-content items-center justify-center
                                            w-full h-full overflow-hidden text-[0] bg-[#f1f2f4] overflow-hidden">
                                        <video loop autoPlay={true} muted
                                               poster="/videos/imageInpaintDemoPoster.png"
                                               className="object-fill w-[100%] h-[100%] pointer-events-none">
                                            <source src="https://yun.urldwz.com/f/XgpKTZ/imageInpaintDemo.mp4" type="video/mp4"/>
                                        </video>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    )
}
